import * as Sentry from '@sentry/react';
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  type FormProps,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { addCommand, bulkCommand, getCurrentAwS } from 'api/devices.api';
import { format } from 'date-fns';
import notificationHook from 'hooks/notificationHook';
import { getDeviceProtocolVersion } from 'libs/deviceProtocol';
import { useEffect, useMemo, useState } from 'react';
import { CommandsType } from 'types/commands';
import { CheckerEmployee, Device } from 'types/devices';
import { DeviceAdministratorFlow } from './DeviceAdministratorFlow';

type BackupCommandRow = {
  cmdId: number;
  label: string;
  status: 'error' | 'successful' | 'pending' | 'timeout';
  queueStatus?: string;
};

type CommandName =
  | 'updateEmployeeInfo'
  | 'queryEmployeeInfo'
  | 'queryEmployeeEvents'
  | 'resetAdministratorPassword'
  | 'correctEmployeePin'
  | 'promoteDeviceAdministrator';

type CommandBuilderValues = {
  command: CommandName;
  employeeId: number;
  weeks?: number;
  administratorPassword?: string;
  confirmAdministratorPassword?: string;
  newEmployeeId?: number;
  confirmBiometricMigration?: boolean;
};

type CustomCommandBuilderProps = {
  device: Device;
  employees: CheckerEmployee[];
  onEmployeesRefresh: () => Promise<void>;
  onClose: () => void;
};

const buildDateRange = (weeks: number) => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  start.setDate(start.getDate() - weeks * 7);
  start.setHours(0, 0, 0, 0);

  return {
    startTime: format(start, 'yyyy-MM-dd HH:mm:ss'),
    endTime: format(end, 'yyyy-MM-dd HH:mm:ss'),
  };
};

const isAdministratorPrivilege = (privilege?: string | number) => {
  if (privilege === undefined || privilege === null) return false;
  const privilegeValue = String(privilege);
  const numericPrivilege = Number(privilegeValue);
  return (
    numericPrivilege === 6 ||
    numericPrivilege === 14 ||
    /admin/i.test(privilegeValue)
  );
};

export const CustomCommandBuilder = ({
  device,
  employees,
  onEmployeesRefresh,
  onClose,
}: CustomCommandBuilderProps) => {
  const [form] = Form.useForm<CommandBuilderValues>();
  const [submitting, setSubmitting] = useState(false);
  const [backingUpBiometrics, setBackingUpBiometrics] = useState(false);
  const [backupCommands, setBackupCommands] = useState<BackupCommandRow[]>([]);
  const command = Form.useWatch('command', form);
  const employeeId = Form.useWatch('employeeId', form);
  const weeks = Form.useWatch('weeks', form);
  const newEmployeeId = Form.useWatch('newEmployeeId', form);
  const destinationPinExists = employees.some(
    (registeredUser) => Number(registeredUser.PIN) === Number(newEmployeeId)
  );
  const pushVersion = device.pushVer || '';
  const protocolVersion = getDeviceProtocolVersion(device);
  const backupFinished =
    backupCommands.length > 0 &&
    backupCommands.every(
      (backupCommand) => backupCommand.queueStatus === 'done'
    );
  const backupHasErrors = backupCommands.some(
    (backupCommand) => backupCommand.status === 'error'
  );
  const backupCommandIds = backupCommands
    .map((backupCommand) => backupCommand.cmdId)
    .join(',');

  useEffect(() => {
    if (!backupCommandIds || backupFinished) return;

    const commandIds = new Set(
      backupCommandIds.split(',').map((commandId) => Number(commandId))
    );
    let intervalId: number | undefined;
    let active = true;

    const refreshCommands = async () => {
      try {
        const response = await getCurrentAwS({
          page: 1,
          limit: 100,
          serialNumber: device.serialNumber,
        });
        if (!active) return;
        const currentCommands = new Map(
          response.data.data
            .filter((currentCommand) => commandIds.has(currentCommand.cmdId))
            .map((currentCommand) => [currentCommand.cmdId, currentCommand])
        );
        setBackupCommands((currentRows) =>
          currentRows.map((currentRow) => {
            const currentCommand = currentCommands.get(currentRow.cmdId);
            return currentCommand
              ? {
                  ...currentRow,
                  status: currentCommand.status,
                  queueStatus: currentCommand.queueStatus,
                }
              : currentRow;
          })
        );
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    void refreshCommands();
    intervalId = window.setInterval(refreshCommands, 3000);

    return () => {
      active = false;
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [backupCommandIds, backupFinished, device.serialNumber]);

  const backupColumns = useMemo<ColumnsType<BackupCommandRow>>(
    () => [
      {
        title: 'Consulta',
        dataIndex: 'label',
        key: 'label',
      },
      {
        title: 'ID',
        dataIndex: 'cmdId',
        key: 'cmdId',
        width: 80,
      },
      {
        title: 'Estado',
        key: 'status',
        width: 130,
        render: (_, row) => {
          if (row.status === 'successful') {
            return <Tag color="green">Finalizado</Tag>;
          }
          if (row.status === 'error') return <Tag color="red">Error</Tag>;
          if (row.status === 'timeout') {
            return <Tag color="orange">Timeout</Tag>;
          }
          if (row.queueStatus === 'processing') {
            return <Tag color="blue">Procesando</Tag>;
          }
          return <Tag color="gold">Pendiente</Tag>;
        },
      },
    ],
    []
  );

  const backupBiometrics = async () => {
    const pin = Number(employeeId);
    if (!Number.isInteger(pin) || pin <= 0) {
      notificationHook({
        type: 'error',
        message: 'Ingresa primero el PIN incorrecto del dispositivo.',
      });
      return;
    }

    const commands: CommandsType[] =
      protocolVersion === 2
        ? [
            {
              serialNumber: device.serialNumber,
              command: 'getFinger',
              employeeId: pin,
            },
            ...Array.from({ length: 11 }, (_, bioType) => ({
              serialNumber: device.serialNumber,
              command: 'getBioData' as const,
              employeeId: pin,
              bioType,
            })),
          ]
        : (['getFinger', 'getBioData', 'getBioPhoto'] as const).map(
            (biometricCommand) => ({
              serialNumber: device.serialNumber,
              command: biometricCommand,
              employeeId: pin,
            })
          );

    setBackingUpBiometrics(true);
    setBackupCommands([]);
    try {
      const response = await bulkCommand(commands);
      const commandIds = response.data.data || [];
      setBackupCommands(
        commandIds.map((cmdId, index) => ({
          cmdId,
          label:
            commands[index]?.command === 'getFinger'
              ? 'Huellas'
              : commands[index]?.command === 'getBioPhoto'
                ? 'Fotografía'
                : protocolVersion === 2
                  ? `Biométrico tipo ${commands[index]?.bioType}`
                  : 'Datos biométricos',
          status: 'pending',
          queueStatus: 'in-queue',
        }))
      );
      notificationHook({
        type: 'success',
        message:
          'Se solicitaron los biométricos del PIN del dispositivo. Espera a que los comandos finalicen antes de corregirlo.',
      });
    } catch (error) {
      notificationHook({
        type: 'error',
        message: 'No fue posible solicitar los biométricos del dispositivo.',
      });
      Sentry.captureException(error);
    } finally {
      setBackingUpBiometrics(false);
    }
  };

  const getPreview = () => {
    if (!command || !employeeId || !protocolVersion) return '';
    if (command === 'updateEmployeeInfo') {
      return protocolVersion === 2
        ? `DATA UPDATE USERINFO PIN=${employeeId}\t<datos del empleado>`
        : `DATA UPDATE user Pin=${employeeId}\t<datos del empleado>`;
    }
    if (command === 'queryEmployeeInfo') {
      return protocolVersion === 2
        ? `DATA QUERY USERINFO PIN=${employeeId}`
        : `DATA QUERY tablename=user,fielddesc=*,filter=Pin=${employeeId}`;
    }
    if (command === 'resetAdministratorPassword') {
      return protocolVersion === 2
        ? `DATA UPDATE USERINFO PIN=${employeeId}\tPasswd=[PROTEGIDO]\t<datos existentes>`
        : `DATA UPDATE user Pin=${employeeId}\tPassword=[PROTEGIDO]\t<datos existentes>`;
    }
    if (command === 'correctEmployeePin') {
      if (!newEmployeeId) return '';
      if (destinationPinExists) {
        return protocolVersion === 2
          ? `DATA DELETE FINGERTMP/BIODATA/BIOPHOTO PIN=${employeeId} → DATA DELETE USERINFO PIN=${employeeId} → restaurar biométricos en PIN=${newEmployeeId}`
          : `DATA DELETE <biométricos PIN=${employeeId}> → DATA DELETE user Pin=${employeeId} → restaurar biométricos en Pin=${newEmployeeId}`;
      }
      return protocolVersion === 2
        ? `DATA DELETE FINGERTMP/BIODATA/BIOPHOTO PIN=${employeeId} → DATA DELETE USERINFO PIN=${employeeId} → DATA UPDATE USERINFO PIN=${newEmployeeId} → restaurar biométricos`
        : `DATA DELETE <biométricos PIN=${employeeId}> → DATA DELETE user Pin=${employeeId} → DATA UPDATE user Pin=${newEmployeeId} → restaurar biométricos`;
    }
    if (!weeks) return '';
    const { startTime, endTime } = buildDateRange(weeks);
    return protocolVersion === 2
      ? `DATA QUERY ATTLOG StartTime=${startTime}\tEndTime=${endTime}`
      : `DATA QUERY tablename=transaction,fielddesc=*,filter=starttime=${startTime}\tendtime=${endTime}\tpin=${employeeId}`;
  };

  const submitCommand = async (values: CommandBuilderValues) => {
    if (!protocolVersion) {
      notificationHook({
        type: 'error',
        message: 'Configura una versión PUSH v2 o v3 antes de continuar.',
      });
      return;
    }
    setSubmitting(true);
    const payload: CommandsType = {
      serialNumber: device.serialNumber,
      command: values.command,
      employeeId: values.employeeId,
      weeks:
        values.command === 'queryEmployeeEvents' ? values.weeks : undefined,
      administratorPassword:
        values.command === 'resetAdministratorPassword'
          ? values.administratorPassword
          : undefined,
      newEmployeeId:
        values.command === 'correctEmployeePin'
          ? values.newEmployeeId
          : undefined,
      confirmBiometricMigration:
        values.command === 'correctEmployeePin'
          ? values.confirmBiometricMigration
          : undefined,
    };

    try {
      const response = await addCommand(payload);
      notificationHook({
        type: 'success',
        message: `Éxito: ${response.data.message}`,
      });
      form.resetFields();
      onClose();
    } catch (error) {
      notificationHook({
        type: 'error',
        message: 'No fue posible agregar el comando al dispositivo.',
      });
      Sentry.captureException(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFailed: NonNullable<
    FormProps<CommandBuilderValues>['onFinishFailed']
  > = (errorInfo) => {
    const firstError = errorInfo.errorFields[0]?.errors[0];
    notificationHook({
      type: 'error',
      message: 'Revisa los datos del comando.',
      description:
        typeof firstError === 'string'
          ? firstError
          : 'Hay campos pendientes o inválidos.',
    });
  };

  const preview = getPreview();

  return (
    <Form<CommandBuilderValues>
      form={form}
      layout="vertical"
      initialValues={{ command: 'updateEmployeeInfo', weeks: 1 }}
      onFinish={submitCommand}
      onFinishFailed={handleSubmitFailed}
      scrollToFirstError={{ block: 'center' }}
      data-cy="custom-command-builder"
    >
      <Alert
        className="mb-5"
        type={protocolVersion ? 'info' : 'warning'}
        showIcon
        message={
          protocolVersion
            ? `Protocolo configurado: v${protocolVersion}`
            : 'Protocolo no disponible'
        }
        description={
          protocolVersion
            ? `Versión reportada: ${pushVersion}`
            : 'Configura una versión PUSH válida de ZK v2 o v3 en Editar dispositivo antes de continuar.'
        }
        data-cy="custom-command-protocol"
      />
      <Form.Item
        label="Comando"
        name="command"
        rules={[{ required: true, message: 'Selecciona un comando.' }]}
      >
        <Select
          options={[
            {
              value: 'updateEmployeeInfo',
              label: 'Actualizar información del empleado',
            },
            {
              value: 'queryEmployeeEvents',
              label: 'Recuperar checadas del empleado',
            },
            {
              value: 'queryEmployeeInfo',
              label: 'Descargar información de un empleado',
            },
            {
              value: 'resetAdministratorPassword',
              label: 'Cambiar PIN de administrador del dispositivo',
            },
            {
              value: 'correctEmployeePin',
              label: 'Corregir PIN del empleado',
            },
            ...(protocolVersion
              ? [
                  {
                    value: 'promoteDeviceAdministrator',
                    label:
                      'Convertir empleado en administrador del dispositivo',
                  },
                ]
              : []),
          ]}
          data-cy="custom-command-type"
        />
      </Form.Item>
      {command === 'promoteDeviceAdministrator' ? (
        <DeviceAdministratorFlow
          device={device}
          employees={employees}
          onEmployeesRefresh={onEmployeesRefresh}
          onClose={onClose}
        />
      ) : (
        <>
          <Form.Item
            label={
              command === 'resetAdministratorPassword'
                ? 'PIN del usuario administrador'
                : command === 'correctEmployeePin'
                  ? 'PIN incorrecto actual'
                  : 'Número de empleado (PIN)'
            }
            name="employeeId"
            rules={[
              { required: true, message: 'Ingresa el PIN del empleado.' },
              {
                type: 'number',
                min: 1,
                message: 'El PIN debe ser un número entero mayor a cero.',
              },
              {
                validator: async (_, value) => {
                  if (
                    ![
                      'resetAdministratorPassword',
                      'correctEmployeePin',
                    ].includes(command) ||
                    !value
                  )
                    return;
                  const user = employees.find(
                    (registeredUser) => Number(registeredUser.PIN) === value
                  );
                  if (!user) {
                    throw new Error(
                      'El PIN debe aparecer en los usuarios descargados del dispositivo, aunque no exista como empleado en ProSinergia.'
                    );
                  }
                  if (
                    command === 'resetAdministratorPassword' &&
                    !isAdministratorPrivilege(user.Pri)
                  ) {
                    throw new Error(
                      'El PIN debe pertenecer a un administrador descargado del dispositivo.'
                    );
                  }
                },
              },
            ]}
          >
            <InputNumber
              min={1}
              precision={0}
              style={{ width: '100%' }}
              data-cy="custom-command-pin"
            />
          </Form.Item>
          {command === 'correctEmployeePin' && (
            <>
              <Alert
                className="mb-5"
                type="warning"
                showIcon
                message="El protocolo no permite renombrar directamente un PIN."
                description="Solicita aquí los biométricos usando el PIN incorrecto; no necesita existir como empleado en ProSinergia. Espera a que los comandos finalicen antes de corregirlo. La corrección eliminará el registro anterior, creará el PIN correcto y restaurará los datos disponibles en el servidor."
              />
              <Form.Item label="Respaldo del PIN incorrecto">
                <Space direction="vertical">
                  <Button
                    type="default"
                    onClick={backupBiometrics}
                    loading={backingUpBiometrics}
                    disabled={!employeeId}
                    data-cy="custom-command-backup-biometrics"
                  >
                    Solicitar biométricos de este PIN
                  </Button>
                  <Typography.Text type="secondary">
                    El estado se actualizará automáticamente en esta ventana.
                  </Typography.Text>
                </Space>
              </Form.Item>
              {backupCommands.length > 0 && (
                <Form.Item label="Estado del respaldo">
                  <Table<BackupCommandRow>
                    size="small"
                    pagination={false}
                    rowKey="cmdId"
                    columns={backupColumns}
                    dataSource={backupCommands}
                    data-cy="custom-command-backup-status"
                  />
                  {backupFinished && (
                    <Alert
                      className="mt-3"
                      type={backupHasErrors ? 'warning' : 'success'}
                      showIcon
                      message={
                        backupHasErrors
                          ? 'El respaldo terminó con consultas fallidas.'
                          : 'El respaldo biométrico terminó correctamente.'
                      }
                    />
                  )}
                </Form.Item>
              )}
              <Form.Item
                label="PIN correcto del empleado"
                name="newEmployeeId"
                dependencies={['employeeId']}
                rules={[
                  { required: true, message: 'Ingresa el PIN correcto.' },
                  {
                    type: 'number',
                    min: 1,
                    message: 'El PIN debe ser un número entero mayor a cero.',
                  },
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (!value) return;
                      if (getFieldValue('employeeId') === value) {
                        throw new Error('El PIN correcto debe ser diferente.');
                      }
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={1}
                  precision={0}
                  style={{ width: '100%' }}
                  data-cy="custom-command-correct-pin"
                />
              </Form.Item>
              {newEmployeeId && destinationPinExists && (
                <Alert
                  className="mb-5"
                  type="warning"
                  showIcon
                  message={`El PIN ${newEmployeeId} ya existe; se conservará ese usuario.`}
                  description={`Se eliminarán primero los biométricos del PIN incorrecto ${employeeId}, después se eliminará ese usuario y su respaldo biométrico se restaurará en el PIN existente ${newEmployeeId}. No se reemplazará la información del usuario existente.`}
                  data-cy="custom-command-pin-merge-warning"
                />
              )}
              <Form.Item
                name="confirmBiometricMigration"
                valuePropName="checked"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (value === true) return;
                      throw new Error(
                        'Confirma el respaldo antes de continuar.'
                      );
                    },
                  },
                ]}
              >
                <Checkbox
                  disabled={!backupFinished}
                  data-cy="custom-command-confirm-pin-correction"
                >
                  {destinationPinExists
                    ? `Confirmo que deseo fusionar el PIN incorrecto ${employeeId} con el PIN existente ${newEmployeeId}; solicité el respaldo y esperé a que terminara.`
                    : 'Confirmo que solicité el respaldo y esperé a que terminara; entiendo que los biométricos no disponibles deberán volver a registrarse.'}
                </Checkbox>
              </Form.Item>
            </>
          )}
          {command === 'resetAdministratorPassword' && (
            <>
              <Alert
                className="mb-5"
                type="warning"
                showIcon
                message="Este comando reemplazará el PIN de acceso al menú."
                description="Primero descarga los empleados del dispositivo para validar la cuenta administradora. El valor nuevo se oculta en la vista previa y en los registros operativos."
              />
              <Form.Item
                label="Nuevo PIN de menú"
                name="administratorPassword"
                rules={[
                  { required: true, message: 'Ingresa el nuevo PIN de menú.' },
                  {
                    pattern: /^\d{1,6}$/,
                    message: 'El PIN debe contener entre 1 y 6 dígitos.',
                  },
                ]}
              >
                <Input.Password
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="new-password"
                  data-cy="custom-command-administrator-password"
                />
              </Form.Item>
              <Form.Item
                label="Confirmar PIN de menú"
                name="confirmAdministratorPassword"
                dependencies={['administratorPassword']}
                rules={[
                  { required: true, message: 'Confirma el nuevo PIN de menú.' },
                  ({ getFieldValue }) => ({
                    validator: async (_, value) => {
                      if (
                        !value ||
                        getFieldValue('administratorPassword') === value
                      )
                        return;
                      throw new Error('Los PIN no coinciden.');
                    },
                  }),
                ]}
              >
                <Input.Password
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="new-password"
                  data-cy="custom-command-administrator-password-confirmation"
                />
              </Form.Item>
            </>
          )}
          {command === 'queryEmployeeEvents' && (
            <>
              <Form.Item
                label="Semanas a recuperar"
                name="weeks"
                rules={[
                  { required: true, message: 'Ingresa el número de semanas.' },
                  {
                    type: 'number',
                    min: 1,
                    max: 52,
                    message: 'Ingresa entre 1 y 52 semanas.',
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  max={52}
                  precision={0}
                  style={{ width: '100%' }}
                  data-cy="custom-command-weeks"
                />
              </Form.Item>
              {protocolVersion === 2 && (
                <Alert
                  className="mb-5"
                  type="warning"
                  showIcon
                  message="El protocolo v2 no permite filtrar ATTLOG por PIN."
                  description="El dispositivo enviará las checadas de todos los empleados dentro del rango; el servidor procesará entre ellas las del PIN solicitado."
                />
              )}
            </>
          )}
          {preview && (
            <Typography.Paragraph>
              <Typography.Text type="secondary">Vista previa</Typography.Text>
              <br />
              <Typography.Text code copyable>
                {preview}
              </Typography.Text>
            </Typography.Paragraph>
          )}
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              data-cy="custom-command-submit"
            >
              Enviar comando
            </Button>
            <Button onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
          </Space>
        </>
      )}
    </Form>
  );
};
